import type { SelectProps } from '@risk-smart/themed-cloudscape-components/select';
import CSelect from '@risk-smart/themed-cloudscape-components/select';
import { forwardRef } from 'react';

import { useVisibleOptions } from '../option-label-filter/useVisibleOptions';
import styles from './style.module.scss';

const Select = forwardRef<SelectProps.Ref, SelectProps>(
  ({ filteringType = 'auto', options, onLoadItems, ...props }, ref) => {
    // See filterOptionsByVisibleText for why we override 'auto' filtering.
    const preFilter = filteringType === 'auto';
    const { visibleOptions, setFilteringText } = useVisibleOptions(
      options,
      preFilter
    );

    // Do NOT suppress the caller's `virtualScroll` on the pre-filtered path.
    // Two attempts have now done that and both caused the same outage from
    // opposite ends:
    //
    //   RSP-6288 disabled it whenever we pre-filter. `filteringType` defaults
    //   to 'auto' and no caller overrides it, so that made every virtualScroll
    //   prop in the app dead code and a 12k-option owner list took 30+s to
    //   open (COI-569).
    //
    //   COI-569's first fix narrowed the suppression to `filteringText !== ''`.
    //   That just moved the stall: one typed character still matches thousands
    //   of a 12k-user tenant, and PlainList mounts one Option component per
    //   match, so every keystroke hung the tab.
    //
    // Virtualisation is the only thing that bounds the render at either size,
    // so it stays on throughout. Cloudscape supports this: `prepareOptions`
    // hands `filteredOptions` to VirtualList identically for 'auto' and
    // 'manual' — nothing in its virtual list branches on filteringType, and
    // `virtual-list.tsx` deliberately keys `estimateSize` on `filteringValue`
    // to re-measure on filter change. The RSP-6288 claim that react-virtual@2
    // "only re-windows when Cloudscape owns the filtering" does not hold.
    //
    // What IS true of manual mode: Cloudscape's own `filteringValue` updates
    // synchronously while our narrowed array arrives a debounced tick later via
    // `onLoadItems`, so one render sees the new filter text over the old array.
    // That gap — not virtualScroll — is the mechanism behind RSP-6288's stale
    // window. If it resurfaces, fix the gap; do not disable virtualScroll.

    return (
      <div className={styles.select}>
        <CSelect
          {...props}
          filteringType={preFilter ? 'manual' : filteringType}
          options={visibleOptions}
          onLoadItems={(event) => {
            if (preFilter) {
              setFilteringText(event.detail.filteringText ?? '');
            }
            onLoadItems?.(event);
          }}
          ref={ref}
        />
      </div>
    );
  }
);
Select.displayName = 'Select';

export default Select;
